import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Github } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MapleOSLogo from "@/components/common/MapleOSLogo";
import SEO from "@/components/common/SEO";
import ArchitectureTeaser from "@/components/landing/ArchitectureTeaser";
import ArchitectureFlowDialog from "@/components/landing/ArchitectureFlowDialog";
import HeroLivingSystem from "@/components/landing/HeroLivingSystem";
import InteractiveDemo from "@/components/landing/InteractiveDemo";
import LandingShell from "@/components/landing/LandingShell";
import PartnerLogoStrip from "@/components/landing/PartnerLogoStrip";
import SystemCards from "@/components/landing/SystemCards";
import HeroSystemPreview from "@/components/landing/HeroSystemPreview";
import UseCases from "@/components/landing/UseCases";
import WhyMapleOS from "@/components/landing/WhyMapleOS";
import { getHomeSeo } from "@/lib/seo/publicMetadata";
import { APP_VERSION } from "@/utils/version";

const springTransition = { type: "spring" as const, duration: 0.5, bounce: 0.15 };

function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.095 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function CTASection() {
  return (
    <section id="download" className="scroll-mt-24 px-6 py-20 md:py-28">
      <div className="mx-auto max-w-6xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={springTransition}
          className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(12,18,31,0.96),rgba(3,7,18,1))] px-6 py-14 shadow-[0_30px_120px_rgba(0,0,0,0.3)] md:px-12 md:py-20"
        >
          <div className="pointer-events-none absolute inset-0" />
          <MapleOSLogo className="mx-auto h-12 w-auto text-primary md:h-16" />
          <h2 className="mt-6 text-4xl font-semibold leading-tight tracking-[-0.05em] text-white md:text-6xl">
            Take back your control.
          </h2>

          <p className="mx-auto mt-6 max-w-3xl text-lg leading-relaxed text-white/60 md:text-2xl">
            Work, write, build, and explore in a system designed around your decisions — not big tech defaults.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/start"
              className="inline-flex min-w-[240px] items-center justify-center rounded-2xl bg-primary px-8 py-4 text-base font-semibold text-primary-foreground shadow-[0_0_32px_hsl(var(--primary)/0.35)] transition-transform duration-200 hover:scale-[1.02]"
            >
              Launch mapleos
            </Link>
            <Link
              to="/downloads"
              className="inline-flex min-w-[240px] items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-8 py-4 text-base font-semibold text-white transition-colors duration-200 hover:bg-white/10"
            >
              Download OS
            </Link>
            <ArchitectureFlowDialog>
              <button
                type="button"
                className="inline-flex min-w-[240px] items-center justify-center rounded-2xl border border-white/10 bg-transparent px-8 py-4 text-base font-semibold text-white/75 transition-colors duration-200 hover:text-white"
              >
                View Architecture
              </button>
            </ArchitectureFlowDialog>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  const socialLinks = [
    { label: "GitHub", href: "https://github.com/CanXPAI/mapleos-your-digital-canvas", icon: Github },
    { label: "Discord", href: "https://discord.gg/Mh9ypjXRU2", icon: DiscordIcon },
    { label: "X", href: "https://x.com/mapleosai", icon: XIcon },
  ];

  return (
    <footer className="border-t border-white/10 px-6 py-12">
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 text-center">
        <div className="flex items-center justify-center gap-6 text-white/55">
          {socialLinks.map(({ label, href, icon: Icon }) => (
            <motion.a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              title={label}
              whileHover={{ y: -2, scale: 1.06 }}
              whileTap={{ scale: 0.96 }}
              className="transition-colors duration-200 hover:text-primary"
            >
              <Icon className="h-6 w-6" />
            </motion.a>
          ))}
        </div>
        <div className="text-sm text-white/45">© 2026 MapleOS. All rights reserved.</div>
        <div className="text-sm text-white/35">v{APP_VERSION}</div>
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/50">

          <Link to="/start" className="transition-colors hover:text-primary">
            Launch
          </Link>
          <Link to="/downloads" className="transition-colors hover:text-primary">
            Downloads
          </Link>
          <Link to="/docs" className="transition-colors hover:text-primary">
            Documentation
          </Link>
          <a
            href="https://github.com/CanXPAI/mapleos-your-digital-canvas"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-primary"
          >
            GitHub
          </a>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const seo = getHomeSeo();

  useEffect(() => {
    if (!hash) {
      return;
    }

    if (hash === "#community") {
      navigate("/community", { replace: true });
      return;
    }

    const id = hash.slice(1);
    let attempts = 0;

    const scrollToHashTarget = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "auto", block: "start" });
        return;
      }

      attempts += 1;
      if (attempts < 10) {
        window.requestAnimationFrame(scrollToHashTarget);
      }
    };

    scrollToHashTarget();
  }, [hash, navigate]);

  return (
    <LandingShell>
      <SEO {...seo} />

      <div className="relative overflow-hidden bg-background text-foreground">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-0 top-0 h-[34rem] w-[34rem] rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-0 top-[28rem] h-[26rem] w-[26rem] rounded-full bg-sky-500/10 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-[26rem] w-[26rem] rounded-full bg-primary/10 blur-3xl" />
        </div>

        <main className="relative z-10">
          <HeroLivingSystem />
          <WhyMapleOS />
          <InteractiveDemo />
          <UseCases />
          <ArchitectureTeaser />
          <PartnerLogoStrip />
          <CTASection />
        </main>

        <Footer />
      </div>
    </LandingShell>
  );
}